import { useState, useEffect, useCallback, useMemo } from 'react';
import { API_BASE_URL } from '../config';
import MainLayout from '../components/layout/MainLayout';

// ─── Types ──────────────────────────────────────────────
interface HubCampaign { campaignId: string; name: string; geo: string; category: string; }
interface RedirectDomain { id: number; domain: string; esp: string; geo: string; channel: string; is_active: boolean; }
interface Network { id: number; name: string; display_name: string; param_template: Record<string, string>; notes: string; link_count?: number; }
interface AffLink {
  id: number; slug: string; name: string; network_id: number; network_name: string; network_display: string;
  param_template: Record<string, string>; base_url: string; geo: string; category: string; link_category: string;
  is_active: boolean; clicks_7d: number; clicks_30d: number; campaign_id?: string; creative_id?: string;
}

const CATEGORIES = ['Loan', 'Creditcard', 'MicroLoan', 'Sweepstake', 'Insurance', 'Nutra', 'Energy', 'Other'];
const CHANNELS = ['Simplyloan', 'CoReg'];
const GEOS = ['FI', 'SE', 'DE', 'NO'];

// ─── Helpers ────────────────────────────────────────────
async function api(path: string, method = 'GET', body?: unknown, retries = 1) {
  const opts: RequestInit = { method, headers: { 'Content-Type': 'application/json' } };
  if (body) opts.body = JSON.stringify(body);

  for (let i = 0; ; i++) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    try {
      const res = await fetch(`${API_BASE_URL}${path}`, { ...opts, signal: controller.signal });
      clearTimeout(timeoutId);
      if (!res.ok) throw new Error(`API error: ${res.status}`);
      const json = await res.json();
      return json?.data ?? json;
    } catch (e) {
      clearTimeout(timeoutId);
      // If blocked by adblock or network error, try fallback alias if applicable
      if (path.startsWith('/api/affiliate/') && i >= retries) {
        try {
          const fallbackPath = path.replace('/api/affiliate/', '/api/partner-links/');
          const res = await fetch(`${API_BASE_URL}${fallbackPath}`, opts);
          if (res.ok) {
            const json = await res.json();
            return json?.data ?? json;
          }
        } catch {
          // Ignore fallback error and throw original
        }
      }
      if (i >= retries) throw e;
      await new Promise(r => setTimeout(r, 400 * (i + 1))); // exponential backoff
    }
  }
}

function buildPreviewUrl(baseUrl: string, template: Record<string, string>, esp = 'fbse', leadId = '123456') {
  if (!template || !baseUrl) return baseUrl;
  const sep = baseUrl.includes('?') ? '&' : '?';
  const t = typeof template === 'string' ? JSON.parse(template) : template;
  if (t._combined) {
    const fmt = (t._format || '{s1}_{s2}').replace('{s1}', esp).replace('{s2}', leadId);
    return `${baseUrl}${sep}${t._combined}=${fmt}`;
  }
  const parts: string[] = [];
  if (t.s1) parts.push(`${t.s1}=${esp}`);
  if (t.s2) parts.push(`${t.s2}=${leadId}`);
  return `${baseUrl}${sep}${parts.join('&')}`;
}

// ─── Component ──────────────────────────────────────────
export default function AffiliateLinks() {
  const [tab, setTab] = useState<'links' | 'networks' | 'clicks' | 'domains'>('links');
  const [links, setLinks] = useState<AffLink[]>([]);
  const [networks, setNetworks] = useState<Network[]>([]);
  const [domains, setDomains] = useState<RedirectDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState<'link' | 'network' | 'domain' | null>(null);
  const [editItem, setEditItem] = useState<any>(null);
  const [search, setSearch] = useState('');
  const [filterGeo, setFilterGeo] = useState('');
  const [filterChannel, setFilterChannel] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [hubCampaigns, setHubCampaigns] = useState<HubCampaign[]>([]);
  const [kvSyncing, setKvSyncing] = useState(false);
  const [clickStats, setClickStats] = useState<any>(null);

  // ─── Data loading (parallel with graceful fallback) ──
  const toArray = (v: unknown) => Array.isArray(v) ? v : (Array.isArray((v as any)?.data) ? (v as any).data : []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [linksRes, netsRes, domsRes, clicksRes, campsRes] = await Promise.allSettled([
        api('/api/affiliate/links'),
        api('/api/affiliate/networks'),
        api('/api/affiliate/domains'),
        api('/api/affiliate/clicks/stats?days=30'),
        fetch(`${API_BASE_URL}/api/text-library/campaigns`)
          .then(r => r.ok ? r.json() : null)
          .catch(() => null)
      ]);

      if (linksRes.status === 'fulfilled' && linksRes.value) {
        setLinks(toArray(linksRes.value));
      }
      if (netsRes.status === 'fulfilled' && netsRes.value) {
        setNetworks(toArray(netsRes.value));
      }
      if (domsRes.status === 'fulfilled' && domsRes.value) {
        setDomains(toArray(domsRes.value));
      }
      if (clicksRes.status === 'fulfilled' && clicksRes.value) {
        setClickStats(clicksRes.value);
      }
      if (campsRes.status === 'fulfilled' && campsRes.value) {
        setHubCampaigns(campsRes.value?.campaigns || []);
      }
    } catch (e) {
      console.error('loadAll exception:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ─── CRUD ─────────────────────────────────────────────
  const saveLink = async (data: Record<string, unknown>) => {
    if (data.id) await api(`/api/affiliate/links/${data.id}`, 'PUT', data);
    else await api('/api/affiliate/links', 'POST', data);
    setShowModal(null); setEditItem(null); loadAll();
  };
  const deleteLink = async (id: number) => { if (!window.confirm('Delete this link?')) return; await api(`/api/affiliate/links/${id}`, 'DELETE'); loadAll(); };
  const saveNetwork = async (data: Record<string, unknown>) => {
    if (data.id) await api(`/api/affiliate/networks/${data.id}`, 'PUT', data);
    else await api('/api/affiliate/networks', 'POST', data);
    setShowModal(null); setEditItem(null); loadAll();
  };
  const deleteNetwork = async (id: number) => { if (!window.confirm('Delete this network?')) return; await api(`/api/affiliate/networks/${id}`, 'DELETE'); loadAll(); };
  const saveDomain = async (data: Record<string, unknown>) => {
    if (data.id) await api(`/api/affiliate/domains/${data.id}`, 'PUT', data);
    else await api('/api/affiliate/domains', 'POST', data);
    setShowModal(null); setEditItem(null); loadAll();
  };
  const deleteDomain = async (id: number) => { if (!window.confirm('Delete this domain?')) return; await api(`/api/affiliate/domains/${id}`, 'DELETE'); loadAll(); };
  const syncKV = async () => { setKvSyncing(true); try { await api('/api/affiliate/kv-sync', 'POST'); } catch {} setKvSyncing(false); };

  // ─── Filters ──────────────────────────────────────────
  const uniqueGeos = useMemo(() => Array.from(new Set(links.map(l => l.geo?.toUpperCase()).filter(Boolean))).sort(), [links]);
  const uniqueChannels = useMemo(() => Array.from(new Set(links.map(l => l.category).filter(Boolean))).sort(), [links]);
  const uniqueCategories = useMemo(() => Array.from(new Set(links.map(l => l.link_category).filter(Boolean))).sort(), [links]);
  const activeFilterCount = [filterGeo, filterChannel, filterCategory].filter(Boolean).length;

  const filtered = useMemo(() => links.filter(l => {
    if (search) {
      const searchLower = search.toLowerCase();
      const slugMatch = (l.slug || '').toLowerCase().includes(searchLower);
      const nameMatch = (l.name || '').toLowerCase().includes(searchLower);
      if (!slugMatch && !nameMatch) return false;
    }
    if (filterGeo && (l.geo || '').toUpperCase() !== filterGeo) return false;
    if (filterChannel && l.category !== filterChannel) return false;
    if (filterCategory && l.link_category !== filterCategory) return false;
    return true;
  }), [links, search, filterGeo, filterChannel, filterCategory]);

  // ─── Render ───────────────────────────────────────────
  return (
    <MainLayout>
      <div className="space-y-6 animate-fade-in pb-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-purple-600">
              🔗 Affiliate Links
            </h2>
            <p className="text-gray-500 mt-1">Manage redirect links, networks & domain mappings</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadAll} disabled={loading} className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors flex items-center gap-1.5 shadow-sm">
              <span className={loading ? "animate-spin" : ""}>🔄</span> Refresh
            </button>
            {tab === 'links' && <button onClick={() => { setEditItem({}); setShowModal('link'); }} className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-semibold text-sm hover:bg-indigo-700 transition-colors shadow-sm">+ New Link</button>}
            {tab === 'networks' && <button onClick={() => { setEditItem({}); setShowModal('network'); }} className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-semibold text-sm hover:bg-indigo-700 transition-colors shadow-sm">+ New Network</button>}
            {tab === 'domains' && (
              <>
                <button onClick={syncKV} disabled={kvSyncing} className="px-4 py-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg font-semibold text-sm hover:bg-amber-100 transition-colors shadow-sm">{kvSyncing ? '⏳ Syncing...' : '🔄 Sync to KV'}</button>
                <button onClick={() => { setEditItem({}); setShowModal('domain'); }} className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-semibold text-sm hover:bg-indigo-700 transition-colors shadow-sm">+ New Domain</button>
              </>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
          {(['links', 'networks', 'clicks', 'domains'] as const).map(t => (
            <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === t ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              {t === 'links' ? `Links (${links.length})` : t === 'networks' ? `Networks (${networks.length})` : t === 'domains' ? `Domains (${domains.length})` : 'Click Stats'}
            </button>
          ))}
        </div>

        {/* ═══ LINKS TAB ═══ */}
        {tab === 'links' && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {/* Filter bar */}
            <div className="flex items-center gap-3 p-4 border-b border-gray-100 flex-wrap">
              <input type="text" placeholder="Search links..." value={search} onChange={e => setSearch(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-52 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
              <select value={filterGeo} onChange={e => setFilterGeo(e.target.value)} className="bg-gray-50 border border-gray-200 text-gray-700 text-sm rounded-lg px-3 py-2">
                <option value="">All Geos</option>
                {uniqueGeos.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
              <select value={filterChannel} onChange={e => setFilterChannel(e.target.value)} className="bg-gray-50 border border-gray-200 text-gray-700 text-sm rounded-lg px-3 py-2">
                <option value="">All Channels</option>
                {uniqueChannels.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className="bg-gray-50 border border-gray-200 text-gray-700 text-sm rounded-lg px-3 py-2">
                <option value="">All Categories</option>
                {uniqueCategories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              {activeFilterCount > 0 && (
                <button onClick={() => { setFilterGeo(''); setFilterChannel(''); setFilterCategory(''); setSearch(''); }}
                  className="text-sm text-red-500 hover:text-red-700 font-medium">✕ Clear</button>
              )}
              <span className="text-sm text-gray-400 ml-auto">{filtered.length} of {links.length}</span>
            </div>

            {/* Table */}
            {loading ? (
              <div className="p-12 text-center text-gray-500">
                <div className="inline-block w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mb-3"></div>
                <div>Loading affiliate links...</div>
              </div>
            ) : links.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                <p className="text-base font-medium text-gray-700 mb-1">No links loaded</p>
                <p className="text-xs text-gray-400 mb-4 max-w-md mx-auto">Could not load links. If an ad-blocker is active, it may block URLs with &quot;affiliate&quot;. Try refreshing or disabling ad-block for localhost.</p>
                <button onClick={loadAll} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-colors">
                  Retry Loading
                </button>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Slug</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Name</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Network</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Geo</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Channel</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Category</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Status</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map(link => {
                    const isSimplyLoan = link.category?.toLowerCase() === 'simplyloan' || link.network_display?.toLowerCase().includes('simplyloan') || link.network_name?.toLowerCase().includes('simplyloan') || link.name?.toLowerCase().includes('simplyloan');
                    const geo = link.geo?.toLowerCase() || 'se';
                    const s1 = `sl${geo}ew`;
                    const slc_tail = `?s1=${s1}&s2={{contact.CID}}&s3=AC-XXXXX&s4=email_${s1}&s5={{contact.UTM_SOURCE}}`;
                    
                    return (
                    <tr key={link.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3"><code className="text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded text-xs font-mono">{isSimplyLoan ? `/slgo/${link.slug}/` : `/go/${link.slug}/`}</code></td>
                      <td className="px-4 py-3 font-medium text-gray-800">{link.name}</td>
                      <td className="px-4 py-3"><span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700">{link.network_display || link.network_name}</span></td>
                      <td className="px-4 py-3 text-center font-semibold text-gray-700">{link.geo?.toUpperCase() || '—'}</td>
                      <td className="px-4 py-3">{link.category ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700">{link.category}</span> : <span className="text-gray-300">—</span>}</td>
                      <td className="px-4 py-3">{link.link_category ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">{link.link_category}</span> : <span className="text-gray-300">—</span>}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${link.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${link.is_active ? 'bg-emerald-500' : 'bg-red-500'}`} />
                          {link.is_active ? 'Active' : 'Off'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1 justify-end">
                          <button onClick={() => { setEditItem(link); setShowModal('link'); }} className="px-2 py-1 text-xs font-medium text-indigo-600 hover:bg-indigo-50 rounded transition-colors">Edit</button>
                          <button onClick={() => navigator.clipboard.writeText(isSimplyLoan ? `/slgo/${link.slug}/${slc_tail}` : `/go/${link.slug}/?s2=<$LeadID$>`)} className="px-2 py-1 text-xs font-medium text-gray-500 hover:bg-gray-100 rounded transition-colors" title="Copy full tracking URL">Copy URL</button>
                          <button onClick={() => deleteLink(link.id)} className="px-2 py-1 text-xs font-medium text-red-500 hover:bg-red-50 rounded transition-colors">×</button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                  {filtered.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-400">No links found.</td></tr>}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* ═══ NETWORKS TAB ═══ */}
        {tab === 'networks' && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase">Name</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase">Parameter Template</th>
                  <th className="text-center px-4 py-3 font-semibold text-gray-600 text-xs uppercase">Links</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase">Notes</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-600 text-xs uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(networks || []).map(n => {
                  const t = typeof n.param_template === 'string' ? JSON.parse(n.param_template) : (n.param_template || {});
                  return (
                    <tr key={n.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 font-semibold text-gray-800">{n.display_name}</td>
                      <td className="px-4 py-3">
                        <code className="text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded font-mono">
                          {t._combined ? `${t._combined}=${t._format || '{s1}_{s2}'}` : `s1→${t.s1 || '?'}, s2→${t.s2 || '?'}`}
                        </code>
                      </td>
                      <td className="px-4 py-3 text-center font-semibold text-gray-700">{n.link_count ?? 0}</td>
                      <td className="px-4 py-3 text-gray-400 text-xs">{n.notes || '—'}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1 justify-end">
                          <button onClick={() => { setEditItem(n); setShowModal('network'); }} className="px-2 py-1 text-xs font-medium text-indigo-600 hover:bg-indigo-50 rounded">Edit</button>
                          <button onClick={() => deleteNetwork(n.id)} className="px-2 py-1 text-xs font-medium text-red-500 hover:bg-red-50 rounded">×</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ═══ CLICK STATS TAB ═══ */}
        {tab === 'clicks' && clickStats && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {[
                { label: 'Total Clicks', value: clickStats.summary?.total_clicks, color: 'text-indigo-600' },
                { label: 'Human', value: clickStats.summary?.human_clicks, color: 'text-emerald-600' },
                { label: 'Bot', value: clickStats.summary?.bot_clicks, color: 'text-red-500' },
                { label: 'Unique Leads', value: clickStats.summary?.unique_leads, color: 'text-purple-600' },
                { label: 'Unique IPs', value: clickStats.summary?.unique_ips, color: 'text-amber-600' },
              ].map(s => (
                <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm text-center">
                  <div className={`text-2xl font-bold ${s.color}`}>{(s.value ?? 0).toLocaleString()}</div>
                  <div className="text-xs font-semibold text-gray-400 uppercase mt-1">{s.label}</div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                <h3 className="font-bold text-gray-700 mb-3">Top Links (30d)</h3>
                <table className="w-full text-sm">
                  <thead><tr><th className="text-left px-2 py-1 text-xs text-gray-500">Link</th><th className="text-right px-2 py-1 text-xs text-gray-500">Clicks</th><th className="text-right px-2 py-1 text-xs text-gray-500">Leads</th></tr></thead>
                  <tbody className="divide-y divide-gray-50">
                    {(clickStats.by_link || []).map((l: any) => (
                      <tr key={l.slug}><td className="px-2 py-1.5"><code className="text-xs text-indigo-600">{l.slug}</code></td><td className="px-2 py-1.5 text-right">{l.clicks}</td><td className="px-2 py-1.5 text-right">{l.unique_leads}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                <h3 className="font-bold text-gray-700 mb-3">By ESP (30d)</h3>
                <table className="w-full text-sm">
                  <thead><tr><th className="text-left px-2 py-1 text-xs text-gray-500">ESP</th><th className="text-right px-2 py-1 text-xs text-gray-500">Clicks</th><th className="text-right px-2 py-1 text-xs text-gray-500">Leads</th></tr></thead>
                  <tbody className="divide-y divide-gray-50">
                    {(clickStats.by_esp || []).map((e: any) => (
                      <tr key={e.esp}><td className="px-2 py-1.5"><span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700">{e.esp}</span></td><td className="px-2 py-1.5 text-right">{e.clicks}</td><td className="px-2 py-1.5 text-right">{e.unique_leads}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ═══ DOMAINS TAB ═══ */}
        {tab === 'domains' && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100">
              <p className="text-sm text-gray-500">Map each domain to its ESP SubID. The Cloudflare Worker uses these for redirect URL parameter injection.</p>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase">Domain</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase">ESP SubID</th>
                  <th className="text-center px-4 py-3 font-semibold text-gray-600 text-xs uppercase">Geo</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase">Channel</th>
                  <th className="text-center px-4 py-3 font-semibold text-gray-600 text-xs uppercase">Status</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-600 text-xs uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {domains.map(d => (
                  <tr key={d.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-semibold text-indigo-600">{d.domain}</td>
                    <td className="px-4 py-3"><code className="text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded font-mono">{d.esp}</code></td>
                    <td className="px-4 py-3 text-center font-semibold text-gray-700">{d.geo?.toUpperCase() || '—'}</td>
                    <td className="px-4 py-3">{d.channel || '—'}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${d.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${d.is_active ? 'bg-emerald-500' : 'bg-red-500'}`} />
                        {d.is_active ? 'Active' : 'Off'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => { setEditItem(d); setShowModal('domain'); }} className="px-2 py-1 text-xs font-medium text-indigo-600 hover:bg-indigo-50 rounded">Edit</button>
                        <button onClick={() => deleteDomain(d.id)} className="px-2 py-1 text-xs font-medium text-red-500 hover:bg-red-50 rounded">×</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {domains.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-gray-400">No domains configured.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* ═══ MODALS ═══ */}
        {showModal === 'link' && networks.length > 0 && (
          <LinkModal item={editItem || {}} networks={networks} hubCampaigns={hubCampaigns} onSave={saveLink} onClose={() => { setShowModal(null); setEditItem(null); }} />
        )}
        {showModal === 'network' && (
          <NetworkModal item={editItem || {}} onSave={saveNetwork} onClose={() => { setShowModal(null); setEditItem(null); }} />
        )}
        {showModal === 'domain' && (
          <DomainModal item={editItem || {}} onSave={saveDomain} onClose={() => { setShowModal(null); setEditItem(null); }} />
        )}
      </div>
    </MainLayout>
  );
}

// ─── Link Modal ─────────────────────────────────────────
function LinkModal({ item, networks, hubCampaigns, onSave, onClose }: {
  item: Partial<AffLink>; networks: Network[]; hubCampaigns: HubCampaign[];
  onSave: (d: Record<string, unknown>) => void; onClose: () => void;
}) {
  const [form, setForm] = useState({
    id: item.id, slug: item.slug || '', name: item.name || '',
    network_id: item.network_id || (networks[0]?.id ?? 0),
    base_url: item.base_url || '', geo: item.geo || '',
    category: item.category || '', link_category: item.link_category || '',
    is_active: item.is_active ?? true,
    campaignId: (item as any).campaign_id || (item as any).campaignId || '',
    creative_id: (item as any).creative_id || '',
  });

  const net = networks.find(n => n.id === form.network_id);
  const template = (() => { try { const t = net?.param_template; return t ? (typeof t === 'string' ? JSON.parse(t) : t) : {}; } catch { return {}; } })();

  const inputCls = "w-full text-sm rounded-lg px-3 py-2.5 border border-gray-200 focus:ring-2 focus:ring-indigo-400 focus:outline-none bg-white";
  const selectCls = "w-full text-sm rounded-lg px-3 py-2.5 border border-gray-200 focus:ring-2 focus:ring-indigo-400 focus:outline-none bg-white";
  const labelCls = "block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5";

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl p-6 w-[540px] max-h-[90vh] overflow-auto border border-gray-200" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-bold text-gray-800 mb-5">{form.id ? 'Edit Link' : 'New Link'}</h2>

        <div className="mb-4">
          <label className={`${labelCls} text-amber-600`}>⚡ Campaign (required)</label>
          <select className={`${selectCls} ${!form.campaignId ? 'border-red-300' : ''}`} value={form.campaignId} onChange={e => setForm(f => ({ ...f, campaignId: e.target.value }))}>
            <option value="">— Select Campaign —</option>
            {hubCampaigns.map(c => <option key={c.campaignId} value={c.campaignId}>{c.name} ({c.geo} · {c.category || '?'})</option>)}
          </select>
          {!form.campaignId && <p className="text-xs text-red-500 mt-1">Required</p>}
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div><label className={labelCls}>Slug</label><input className={inputCls} placeholder="lendo" value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))} /></div>
          <div><label className={labelCls}>Name</label><input className={inputCls} placeholder="Lendo SE" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
        </div>

        <div className="mb-4"><label className={labelCls}>Network</label>
          <select className={selectCls} value={form.network_id} onChange={e => setForm(f => ({ ...f, network_id: Number(e.target.value) }))}>{networks.map(n => <option key={n.id} value={n.id}>{n.display_name}</option>)}</select>
        </div>

        <div className="mb-4"><label className={labelCls}>Base URL</label><input className={inputCls} placeholder="https://track..." value={form.base_url} onChange={e => setForm(f => ({ ...f, base_url: e.target.value }))} /></div>

        <div className="grid grid-cols-3 gap-4 mb-4">
          <div><label className={labelCls}>Geo</label><select className={selectCls} value={form.geo} onChange={e => setForm(f => ({ ...f, geo: e.target.value }))}><option value="">—</option>{GEOS.map(g => <option key={g} value={g}>{g}</option>)}</select></div>
          <div><label className={labelCls}>Channel</label><select className={selectCls} value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}><option value="">—</option>{CHANNELS.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
          <div><label className={labelCls}>Category</label><select className={selectCls} value={form.link_category} onChange={e => setForm(f => ({ ...f, link_category: e.target.value }))}><option value="">—</option>{CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
        </div>

        <div className="mb-4">
          <label className={labelCls}>Creative ID</label>
          <input className={inputCls} placeholder="e.g. 253367" value={form.creative_id} onChange={e => setForm(f => ({ ...f, creative_id: e.target.value }))} />
          {net && template.s5 && form.creative_id && <p className="text-xs text-gray-400 mt-1">Auto-injected as s5 on every click → comes back as pubidsub5 in WCT</p>}
        </div>

        <label className="flex items-center gap-2 text-sm font-medium text-gray-600 mb-4"><input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} className="rounded" /> Active</label>

        {form.base_url && (
          <div className="mb-4"><label className={labelCls}>Preview (ESP=fbse)</label>
            <div className="text-xs text-indigo-600 bg-indigo-50 p-3 rounded-lg break-all font-mono">{buildPreviewUrl(form.base_url, template, 'fbse', '123456')}</div>
          </div>
        )}
        {form.slug && (
          <div className="mb-5"><label className={labelCls}>Mailer URL</label>
            <div className="text-xs text-gray-600 bg-gray-50 p-3 rounded-lg font-mono">
              {(() => {
                const networkObj = networks.find(n => n.id === Number(form.network_id));
                const isSimplyLoan = form.category?.toLowerCase() === 'simplyloan' || networkObj?.name?.toLowerCase().includes('simplyloan') || form.name?.toLowerCase().includes('simplyloan');
                return isSimplyLoan 
                  ? `domain.com/slgo/${form.slug}/?s1=sl${form.geo?.toLowerCase() || 'se'}em&s2={{contact.CID}}&s3=AC-XXXXX&s4=email_sl${form.geo?.toLowerCase() || 'se'}em&s5={{contact.UTM_SOURCE}}`
                  : `domain.com/go/${form.slug}/?s2=<$LeadID$>`;
              })()}
            </div>
          </div>
        )}

        <div className="flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={() => onSave({...form, campaign_id: form.campaignId, creative_id: form.creative_id})} disabled={!form.slug || !form.name || !form.base_url || !form.campaignId}
            className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed">Save</button>
        </div>
      </div>
    </div>
  );
}

// ─── Network Modal ──────────────────────────────────────
function NetworkModal({ item, onSave, onClose }: {
  item: Partial<Network>; onSave: (d: Record<string, unknown>) => void; onClose: () => void;
}) {
  const defaultTemplate = item.param_template || { s1: '', s2: '' };
  const isCombined = !!(typeof defaultTemplate === 'object' && (defaultTemplate as Record<string, string>)._combined);
  const t = typeof defaultTemplate === 'object' ? defaultTemplate as Record<string, string> : {};
  const [form, setForm] = useState({
    id: item.id, name: item.name || '', display_name: item.display_name || '', notes: item.notes || '',
    mode: isCombined ? 'combined' : 'separate',
    s1_param: isCombined ? '' : (t.s1 || ''), s2_param: isCombined ? '' : (t.s2 || ''),
    s3_param: isCombined ? '' : (t.s3 || ''), s4_param: isCombined ? '' : (t.s4 || ''), s5_param: isCombined ? '' : (t.s5 || ''),
    combined_param: isCombined ? t._combined || '' : '', combined_format: isCombined ? t._format || '{s1}_{s2}' : '{s1}_{s2}',
  });

  const buildTemplate = () => {
    if (form.mode === 'combined') return { _combined: form.combined_param, _format: form.combined_format };
    const tmpl: Record<string, string> = {};
    if (form.s1_param) tmpl.s1 = form.s1_param; if (form.s2_param) tmpl.s2 = form.s2_param;
    if (form.s3_param) tmpl.s3 = form.s3_param; if (form.s4_param) tmpl.s4 = form.s4_param;
    if (form.s5_param) tmpl.s5 = form.s5_param;
    return tmpl;
  };

  const inputCls = "w-full text-sm rounded-lg px-3 py-2.5 border border-gray-200 focus:ring-2 focus:ring-indigo-400 focus:outline-none bg-white";
  const selectCls = inputCls;
  const labelCls = "block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5";

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl p-6 w-[540px] max-h-[90vh] overflow-auto border border-gray-200" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-bold text-gray-800 mb-5">{form.id ? 'Edit Network' : 'New Network'}</h2>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div><label className={labelCls}>Slug</label><input className={inputCls} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))} /></div>
          <div><label className={labelCls}>Display Name</label><input className={inputCls} value={form.display_name} onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))} /></div>
        </div>
        <div className="mb-4"><label className={labelCls}>Parameter Mode</label>
          <select className={selectCls} value={form.mode} onChange={e => setForm(f => ({ ...f, mode: e.target.value }))}>
            <option value="separate">Separate (s1→param1, s2→param2)</option>
            <option value="combined">Combined (single param)</option>
          </select>
        </div>
        {form.mode === 'separate' ? (
          <>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div><label className={labelCls}>s1 (ESP) → param</label><input className={inputCls} value={form.s1_param} onChange={e => setForm(f => ({ ...f, s1_param: e.target.value }))} /></div>
              <div><label className={labelCls}>s2 (Lead ID) → param</label><input className={inputCls} value={form.s2_param} onChange={e => setForm(f => ({ ...f, s2_param: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div><label className={labelCls}>s3 → param</label><input className={inputCls} value={form.s3_param} onChange={e => setForm(f => ({ ...f, s3_param: e.target.value }))} /></div>
              <div><label className={labelCls}>s4 → param</label><input className={inputCls} value={form.s4_param} onChange={e => setForm(f => ({ ...f, s4_param: e.target.value }))} /></div>
              <div><label className={labelCls}>s5 → param</label><input className={inputCls} value={form.s5_param} onChange={e => setForm(f => ({ ...f, s5_param: e.target.value }))} /></div>
            </div>
          </>
        ) : (
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div><label className={labelCls}>Param name</label><input className={inputCls} value={form.combined_param} onChange={e => setForm(f => ({ ...f, combined_param: e.target.value }))} /></div>
            <div><label className={labelCls}>Format</label><input className={inputCls} value={form.combined_format} onChange={e => setForm(f => ({ ...f, combined_format: e.target.value }))} /></div>
          </div>
        )}
        <div className="mb-4"><label className={labelCls}>Notes</label><input className={inputCls} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
        <div className="mb-5"><label className={labelCls}>Preview</label>
          <div className="text-xs text-indigo-600 bg-indigo-50 p-3 rounded-lg font-mono">
            https://example.com/track?{form.mode === 'combined' ? `${form.combined_param || 'r'}=${form.combined_format.replace('{s1}', 'fbse').replace('{s2}', '123456')}` : `${form.s1_param || 's1'}=fbse&${form.s2_param || 's2'}=123456`}
          </div>
        </div>
        <div className="flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={() => onSave({ ...form, param_template: buildTemplate() })} disabled={!form.name || !form.display_name}
            className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-40">Save</button>
        </div>
      </div>
    </div>
  );
}

// ─── Domain Modal ───────────────────────────────────────
function DomainModal({ item, onSave, onClose }: {
  item: Partial<RedirectDomain>; onSave: (d: Record<string, unknown>) => void; onClose: () => void;
}) {
  const [form, setForm] = useState({
    id: item.id, domain: item.domain || '', esp: item.esp || '',
    geo: item.geo || '', channel: item.channel || '', is_active: item.is_active ?? true,
  });
  const inputCls = "w-full text-sm rounded-lg px-3 py-2.5 border border-gray-200 focus:ring-2 focus:ring-indigo-400 focus:outline-none bg-white";
  const labelCls = "block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5";

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl p-6 w-[440px] border border-gray-200" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-bold text-gray-800 mb-5">{form.id ? 'Edit Domain' : 'New Domain'}</h2>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div><label className={labelCls}>Domain</label><input className={inputCls} placeholder="example.com" value={form.domain} onChange={e => setForm(f => ({ ...f, domain: e.target.value }))} /></div>
          <div><label className={labelCls}>ESP SubID</label><input className={inputCls} placeholder="fbse" value={form.esp} onChange={e => setForm(f => ({ ...f, esp: e.target.value }))} /></div>
        </div>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div><label className={labelCls}>Geo</label><select className={inputCls} value={form.geo} onChange={e => setForm(f => ({ ...f, geo: e.target.value }))}><option value="">—</option>{GEOS.map(g => <option key={g} value={g}>{g}</option>)}</select></div>
          <div><label className={labelCls}>Channel</label><select className={inputCls} value={form.channel} onChange={e => setForm(f => ({ ...f, channel: e.target.value }))}><option value="">—</option>{CHANNELS.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
        </div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-600 mb-5"><input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} className="rounded" /> Active</label>
        <div className="flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={() => onSave(form)} disabled={!form.domain || !form.esp}
            className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-40">Save</button>
        </div>
      </div>
    </div>
  );
}
